import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, Truck, Store, Calendar, ShieldCheck } from "lucide-react";
import api, { inr, formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const SLOTS = [
  { id: "same-day", label: "Same Day Delivery", icon: Truck, note: "Order before 4 PM • ₹49" },
  { id: "next-day", label: "Next Day Delivery", icon: Calendar, note: "Free above ₹499, else ₹29" },
  { id: "store-pickup", label: "Store Pickup", icon: Store, note: "Collect from our Sitapur store • Free" },
];

export default function Checkout() {
  const { cart, cartSubtotal, clearCart } = useStore();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState({ name: "", phone: "", email: "", address: "", pincode: "", landmark: "" });
  const [slot, setSlot] = useState("same-day");
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [pinError, setPinError] = useState("");

  const discount = applied?.discount || 0;
  const deliveryCharge = slot === "store-pickup" ? 0 : slot === "same-day" ? 49 : cartSubtotal >= 499 ? 0 : 29;
  const total = Math.max(0, cartSubtotal - discount + deliveryCharge);

  if (cart.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-4xl text-forest mb-4">Your cart is empty</h1>
        <Button onClick={() => navigate("/products")} className="bg-forest text-cream rounded-full">Shop Now</Button>
      </div>
    );
  }

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon.trim(), subtotal: cartSubtotal });
      setApplied(data);
      toast.success(`Coupon applied — you saved ${inr(data.discount)}!`);
    } catch (e) { setApplied(null); toast.error(formatApiError(e)); }
  };

  const set = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));

  const placeOrder = async () => {
    if (!customer.name || !customer.phone || !customer.address || !customer.pincode) {
      toast.error("Please fill all required delivery details"); return;
    }
    if (customer.phone.replace(/\D/g, "").length < 10) { toast.error("Enter a valid 10-digit phone"); return; }
    setPlacing(true); setPinError("");
    try {
      const { data } = await api.post("/orders", {
        items: cart.map((i) => ({ product_id: i.product_id, name: i.name, variant: i.variant, qty: i.qty, price: i.price, image: i.image })),
        customer, delivery_slot: slot, payment_method: "COD",
        coupon_code: applied?.code || null,
        subscription: cart.some((i) => i.subscription),
      });
      clearCart();
      navigate(`/order-confirmation/${data.order_number}`);
    } catch (e) {
      const msg = formatApiError(e);
      if (msg.includes("Sitapur")) setPinError(msg);
      toast.error(msg);
    } finally { setPlacing(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-12 fade-in">
      <h1 className="font-serif text-5xl text-forest mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          {/* Delivery details */}
          <section>
            <h2 className="font-serif text-2xl text-forest mb-4">Delivery Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input placeholder="Full Name *" value={customer.name} onChange={(e) => set("name", e.target.value)} data-testid="checkout-name" className="rounded-lg" />
              <Input placeholder="Phone Number *" value={customer.phone} onChange={(e) => set("phone", e.target.value)} data-testid="checkout-phone" className="rounded-lg" />
              <Input placeholder="Email (optional)" value={customer.email} onChange={(e) => set("email", e.target.value)} data-testid="checkout-email" className="rounded-lg" />
              <Input placeholder="Pincode *" value={customer.pincode} maxLength={6} onChange={(e) => set("pincode", e.target.value)} data-testid="checkout-pincode" className="rounded-lg" />
              <Input placeholder="Full Address *" value={customer.address} onChange={(e) => set("address", e.target.value)} data-testid="checkout-address" className="rounded-lg sm:col-span-2" />
              <Input placeholder="Landmark (optional)" value={customer.landmark} onChange={(e) => set("landmark", e.target.value)} data-testid="checkout-landmark" className="rounded-lg sm:col-span-2" />
            </div>
            {pinError && <p className="text-sm text-destructive mt-3" data-testid="checkout-pin-error">{pinError}</p>}
          </section>

          {/* Delivery slot */}
          <section>
            <h2 className="font-serif text-2xl text-forest mb-4">Delivery Slot</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {SLOTS.map((s) => (
                <button key={s.id} onClick={() => setSlot(s.id)} data-testid={`slot-${s.id}`}
                  className={`text-left p-4 rounded-xl border transition-colors ${slot === s.id ? "border-gold bg-gold/10" : "border-border hover:border-gold/50"}`}>
                  <s.icon size={20} className="text-forest mb-2" />
                  <div className="font-medium text-forest text-sm">{s.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.note}</div>
                </button>
              ))}
            </div>
          </section>

          {/* Payment */}
          <section>
            <h2 className="font-serif text-2xl text-forest mb-4">Payment Method</h2>
            <div className="p-4 rounded-xl border border-gold bg-gold/10 flex items-center gap-3" data-testid="payment-cod">
              <ShieldCheck className="text-forest" />
              <div>
                <div className="font-medium text-forest text-sm">Cash on Delivery (COD)</div>
                <div className="text-xs text-muted-foreground">Pay in cash when your order arrives. Online payments coming soon.</div>
              </div>
            </div>
          </section>
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <div className="bg-white border border-border rounded-2xl p-6">
            <h2 className="font-serif text-2xl text-forest mb-4">Order Summary</h2>
            <div className="space-y-3 max-h-52 overflow-y-auto no-scrollbar mb-4">
              {cart.map((i) => (
                <div key={`${i.product_id}-${i.variant}`} className="flex gap-3 text-sm">
                  <img src={i.image} alt={i.name} className="w-12 h-12 rounded-md object-cover bg-sand" />
                  <div className="flex-1"><div className="text-forest leading-tight">{i.name}</div><div className="text-xs text-muted-foreground">{i.variant} × {i.qty}</div></div>
                  <div className="text-forest">{inr(i.price * i.qty)}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mb-4">
              <div className="flex-1 flex items-center gap-2 border border-border rounded-full px-3">
                <Tag size={15} className="text-gold" />
                <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="coupon-input" className="flex-1 py-2 text-sm outline-none bg-transparent" />
              </div>
              <Button onClick={applyCoupon} data-testid="coupon-apply" variant="outline" className="rounded-full border-forest text-forest hover:bg-forest hover:text-cream">Apply</Button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Try AADVAN10, WELCOME150 or FESTIVE20</p>

            <div className="space-y-2 text-sm border-t border-border pt-4">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="text-forest">{inr(cartSubtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-forest" data-testid="summary-discount"><span>Discount ({applied.code})</span><span>− {inr(discount)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="text-forest">{deliveryCharge === 0 ? "Free" : inr(deliveryCharge)}</span></div>
              <div className="flex justify-between font-serif text-xl text-forest border-t border-border pt-3 mt-2"><span>Total</span><span data-testid="summary-total">{inr(total)}</span></div>
            </div>

            <Button onClick={placeOrder} disabled={placing} data-testid="place-order-button" className="w-full mt-5 bg-gold text-forest hover:bg-gold-soft rounded-full h-12 text-sm uppercase tracking-[0.15em]">
              {placing ? "Placing Order…" : `Place Order • ${inr(total)}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
